import { MobileContext } from '@/context';
import { consume } from '@lit/context';
import { t } from '@weavedev/lit-i18next';
import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

type Constructor<T> = { new (...args: any[]): T };

export const FocusManagementMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class FocusManagementMixinClass extends superClass {
    private _nextStepButtonRef: Ref<HTMLElement> = createRef();

    @state()
    protected focusFirstOption: boolean = false;

    @state()
    protected showGoToNextStepButton: boolean = false;

    @property({ type: String })
    focusTriggerProperty?: string;

    @property({ type: Boolean })
    open?: boolean;

    @consume({ context: MobileContext, subscribe: true })
    isMobile: boolean = false;

    /**
     * Sets up the focus management for the component
     *
     * @param triggerProperty - The property name to watch for changes
     */
    setupFocusManagement(triggerProperty: string) {
      this.focusTriggerProperty = triggerProperty;
    }

    /**
     * Handles focus management in the willUpdate lifecycle method
     *
     * @param _changedProperties - Map of changed properties
     */
    protected willUpdate(_changedProperties: PropertyValues) {
      super.willUpdate(_changedProperties);

      if (
        this.focusTriggerProperty &&
        _changedProperties.has(this.focusTriggerProperty) &&
        this.open
      ) {
        this.showGoToNextStepButton = true;
      }

      if (
        _changedProperties.has('showGoToNextStepButton') &&
        this.showGoToNextStepButton
      ) {
        this.updateComplete.then(() => {
          const element = this._nextStepButtonRef?.value;
          if (element) {
            if (!this.isMobile) {
              element.focus();
            }

            element.addEventListener('blur', () => {
              this.showGoToNextStepButton = false;
            });
            if (this.isMobile) {
              this.dispatchEvent(
                new CustomEvent('focus-to-next-step', { composed: true }),
              );
            }
          }
        });
      }
    }

    /**
     * Sets the focusFirstOption flag to true and then back to false after a timeout
     */
    focusFirstOptionElement() {
      this.focusFirstOption = true;
      this.updateComplete.then(() => {
        this.focusFirstOption = false;
      });
    }

    /**
     * Renders the "Proceed to Next Step" button if showGoToNextStepButton is true
     * @param stepName - Name of the configurator step
     *
     * @returns A TemplateResult containing the button or nothing
     */
    renderNextStepButton(stepName = ''): TemplateResult | typeof nothing {
      return this.showGoToNextStepButton
        ? html`
            <button
              ${ref(this._nextStepButtonRef as Ref<HTMLButtonElement>)}
              class="sr-only"
              @click=${this.focusFirstOptionElement}
            >
              ${t('actions.proceedFirstOption', { stepName })}
            </button>
          `
        : nothing;
    }
  }

  return FocusManagementMixinClass;
};
